import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/common/Header";
import Sidebar from "../components/common/Sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchProfileCompletion as fetchProfileCompletionApi } from "../api/dashboard";
import { Link } from "react-router-dom";
import { usePlanStatus } from "../contexts/PlanStatusContext";

function readPercent(payload: any): number {
  const v =
    payload?.completion_percentage ??
    payload?.percent ??
    payload?.percentage ??
    payload?.completion ??
    payload?.progress ??
    payload?.value ??
    0;
  const pct = Math.max(0, Math.min(100, Number(v) || 0));
  return Math.round(pct);
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  onProfileChange?: (isOpen: boolean) => void;
}

export default function DashboardLayout({ children, onProfileChange }: DashboardLayoutProps) {
  const [profileClicked, setProfileClicked] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // profile completion
  const [completionPct, setCompletionPct] = useState(0);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [completionError, setCompletionError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { isInactive } = usePlanStatus();

  const isProfileRoute = useMemo(
    () =>
      location.pathname.startsWith("/dashboard/profile/") ||
      location.pathname.startsWith("/dashboard/chat"),
    // ||
    // location.pathname.startsWith("/dashboard/notification"),
    [location.pathname]
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add("h-full", "overflow-hidden");
    body.classList.add("h-full", "overflow-hidden");
    const root = document.getElementById("root");
    if (root) root.classList.add("h-full");
    return () => {
      html.classList.remove("h-full", "overflow-hidden");
      body.classList.remove("h-full", "overflow-hidden");
      if (root) root.classList.remove("h-full");
    };
  }, []);

  useEffect(() => {
    setProfileClicked(isProfileRoute);
  }, [isProfileRoute]);

  const loadProfileCompletion = async () => {
    try {
      setCompletionLoading(true);
      setCompletionError("");
      const data = await fetchProfileCompletionApi();
      setCompletionPct(readPercent(data));
    } catch (e: any) {
      setCompletionError(e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||e?.detail|| "Failed to load profile completion");
      setCompletionPct(0);
    } finally {
      setCompletionLoading(false);
    }
  };

  useEffect(() => {
    if(isProfileRoute)
      loadProfileCompletion();
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => loadProfileCompletion();
    window.addEventListener("roott:profile-updated", handler);
    window.addEventListener("roott:profile-photo-updated", handler);
    return () => {
      window.removeEventListener("roott:profile-updated", handler);
      window.removeEventListener("roott:profile-photo-updated", handler);
    };
  }, []);

  const handleProfileClicked = () => {
    setProfileClicked((prev) => {   
      const next = !prev;
      onProfileChange?.(next);
      if (next) {
        if (!isProfileRoute) navigate("/dashboard/profile/details");
      }
      // else {
      //   navigate("/dashboard");
      // }
      return next;
    });
  };

  const handleNotificationClicked = () => {
    navigate("/dashboard/notification");
  };

  const handleSidebarToggle = () => setSidebarCollapsed((prev) => !prev);

  return (
    <div className="h-full flex flex-col bg-[#F3F4F6]">
      <Header
        onProfileClick={handleProfileClicked}
        onNotificationClick={handleNotificationClicked}
      />

      <div className="flex flex-1 overflow-hidden gap-0 relative">
        {!isInactive && (
          <Sidebar
            onProfileClick={profileClicked}
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={handleSidebarToggle}
          />
        )}

        <main
          className={`flex-1 overflow-auto py-0 pr-1 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-0" : ""
            }`}
        >
          {isProfileRoute && (
            <div className="px-4 py-3 sticky top-0 bg-[#F3F4F6] z-[2]">
              <div className="max-w-[1180px] mx-auto rounded-lg border border-[#E5E7EB] bg-white p-3 flex items-center gap-3">
                <span className="material-icons text-[22px] text-emerald-600">verified</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-sm sm:text-base">Profile completion</div>
                    <div className="text-xs sm:text-sm text-[#6B7280]">
                      {completionLoading ? "…" : `${completionPct}%`}
                    </div>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-green-500 transition-all duration-500"
                      style={{ width: `${completionPct}%` }}
                    />
                  </div>
                  {completionError && (
                    <div className="text-xs text-red-600 mt-1">{completionError}</div>
                  )}
                </div>
                <button
                  title="Refresh"
                  onClick={loadProfileCompletion}
                  className="h-8 w-8 rounded border border-[#E5E7EB] grid place-items-center"
                >
                  <span className="material-icons text-[18px] text-[#6B7280]">refresh</span>
                </button>
              </div>
            </div>
          )}

          {children}
        </main>
      </div>

      <footer className="border-t bg-white">
        <div className="max-w-[1180px] mx-auto px-4 py-6 text-sm text-[#2c2c2c]/70 flex flex-wrap gap-4 justify-center">
          <span>© 2025 Roott. All rights reserved.</span>
          <Link to="privacy-policy">Privacy Policy</Link>
                <Link to="terms-and-conditions">Terms and Conditions</Link>
                {/* <span>Help</span> */}
  
        </div>
      </footer>
    </div>
  );
}
